import { cn } from '@neo/lib/utils'
import { Typography } from '@neo/primitives/typography'
import { Bot, Headset } from 'lucide-react'
import { FormattedMessage } from 'v2/shared/components/formatted-message'
import dayjs from '../../../../utils/dayjs'
import { getAgentTimezone } from '../../../conversations/utils'
import {
  type ChatMessageAttributes,
  type FileMessageAttributes,
  isChatMenuButtons,
  isChatMessageAttributes,
  isChatMultiProduct,
  isChatTextMessage,
  isFileMessageAttributes,
  isMessageAttributesArray,
  isTextAgentChannel,
  type MessageAttributes,
} from '../types'
import { AIAvatar } from './ai-avatar'
import { ChatMenuButtonsComponent } from './chat-menu-buttons'
import { ChatMultiProductCarousel } from './chat-multi-product-carousel'
import { MessageButtons } from './message-buttons'
import { MessageImages } from './message-images'

interface MessageBubbleProps {
  text: string
  // `agent` is a human agent on a chat handoff: the assistant's side of the conversation but
  // not its avatar — unhandled, it drew a person's words behind the AI badge.
  sender: 'user' | 'assistant' | 'agent'
  timestamp?: string
  attributes?: MessageAttributes[] | FileMessageAttributes | ChatMessageAttributes | null
  isLastChunk?: boolean
  // WhatsApp, SMS and Slack render here too, so the handoff avatar is gated on chat rather
  // than on the sender alone — they keep the rendering they have.
  channel?: string | null
}

const messageBodyClassName =
  'font-inter text-foreground dark:[color-scheme:dark] rounded-lg px-4 py-3 max-w-[280px] break-words [overflow-wrap:anywhere] [&_code]:font-jetbrains [&_code]:text-xs-400 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:border [&_pre]:border-border [&_pre]:bg-card [&_pre]:p-2 [&_pre]:font-jetbrains [&_pre]:text-xs-400 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_th]:border-b [&_th]:border-border [&_th]:p-2 [&_th]:text-left [&_th]:text-xs-500 [&_td]:border-b [&_td]:border-border [&_td]:p-2 [&_td]:text-s-400 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-secondary [&_a]:text-brand-text [&_img]:max-w-full [&_img]:rounded'

export const MessageBubble = ({
  text,
  sender,
  timestamp,
  attributes,
  isLastChunk,
  channel,
}: MessageBubbleProps) => {
  const formatTimestamp = (ts: string) => {
    const agentTimezone = getAgentTimezone()
    return dayjs.utc(ts).tz(agentTimezone).format('h:mm A')
  }

  const fileAttributes = isFileMessageAttributes(attributes) ? attributes : null

  const chatMessageAttributes = isChatMessageAttributes(attributes) ? attributes : null
  const chatMenuButtonsContent =
    chatMessageAttributes && isChatMenuButtons(chatMessageAttributes.content)
      ? chatMessageAttributes.content
      : null
  const chatMultiProductContent =
    chatMessageAttributes && isChatMultiProduct(chatMessageAttributes.content)
      ? chatMessageAttributes.content
      : null

  const messageAttributes = isMessageAttributesArray(attributes) ? attributes : null

  const hasContentToShow =
    text || fileAttributes || messageAttributes || chatMenuButtonsContent || chatMultiProductContent

  if (!hasContentToShow) return null

  if (sender === 'user') {
    return (
      <div className="flex flex-col items-end gap-1 pr-1">
        <div data-testid="customer-bubble" className={cn(messageBodyClassName, 'bg-brand/5')}>
          <FormattedMessage
            content={text}
            markdown
            className="text-foreground [&_p]:mb-0 [&_p:last-child]:mb-0"
            disallowedElements={[]}
          />
          {fileAttributes && <MessageImages files={fileAttributes.files} />}
        </div>
        {timestamp && (
          <Typography variant="XS/400" className="font-jetbrains tabular-nums text-secondary mr-2">
            {formatTimestamp(timestamp)}
          </Typography>
        )}
      </div>
    )
  }

  const isPresetBotMessage =
    isChatMessageAttributes(attributes) &&
    isChatTextMessage(attributes.content) &&
    attributes.content.sender === 'bot'

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-end gap-2">
        {sender === 'agent' && isTextAgentChannel(channel) ? (
          <div className="size-8 shrink-0 rounded-full bg-bg2 text-secondary flex items-center justify-center">
            <Headset size={20} />
          </div>
        ) : isPresetBotMessage ? (
          <div className="size-8 shrink-0 rounded-full bg-bg2 text-secondary flex items-center justify-center">
            <Bot size={20} />
          </div>
        ) : (
          <AIAvatar />
        )}
        <div className="min-w-0 flex-1">
          {text && (
            <div data-testid="business-bubble" className={cn(messageBodyClassName, 'bg-muted')}>
              <FormattedMessage
                content={text}
                markdown
                className="text-foreground"
                disallowedElements={[]}
              />
            </div>
          )}
          {fileAttributes && (
            <div data-testid="business-bubble" className={cn(messageBodyClassName, 'bg-muted')}>
              <MessageImages files={fileAttributes.files} />
            </div>
          )}
          <div className="flex gap-2 mt-2 flex-wrap">
            {messageAttributes && messageAttributes.length > 0 && (
              <MessageButtons
                buttons={messageAttributes[0].buttons}
                isLastChunk={isLastChunk || false}
              />
            )}
            {chatMenuButtonsContent && (
              <ChatMenuButtonsComponent
                content={chatMenuButtonsContent}
                isLastChunk={isLastChunk || false}
              />
            )}
            {chatMultiProductContent && (
              <ChatMultiProductCarousel
                content={chatMultiProductContent}
                isLastChunk={isLastChunk || false}
                fullWidth
              />
            )}
          </div>
        </div>
      </div>
      {timestamp && (
        <div className="flex items-center gap-2">
          <div className="w-[30px]" />
          <Typography variant="XS/400" className="font-jetbrains tabular-nums text-secondary ml-2">
            {formatTimestamp(timestamp)}
          </Typography>
        </div>
      )}
    </div>
  )
}
